import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Typography, Spin, Button, Flex, Row, Col, Grid, message } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import { RestaurantService } from "../services/RestaurantService";
import { OrderService } from "../services/OrderService";
import { Restaurant } from "../models/Restaurant";
import { MenuItem } from "../models/MenuItem";
import { OrderItem } from "../models/OrderItem";
import { Flavour } from "../models/Flavour";
import MenuItemModal from "../modals/MenuItemModal";
import TopNavigation, { HEADER_HEIGHT } from "./snipets/TopNavigation";
import Footer from "./snipets/Footer";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const BRAND       = "#00AF66";
const BRAND_LIGHT = "#E6F7EF";

// ── Availability helpers — pure logic, no framework dependency ──────────────
const isWithinAvailabilityWindow = (item: MenuItem): boolean => {
    if (!item.available_from || !item.available_till) return true;
    const now  = new Date();
    const nowM = now.getHours() * 60 + now.getMinutes();
    const [fh, fm] = item.available_from.split(':').map(Number);
    const [th, tm] = item.available_till.split(':').map(Number);
    return nowM >= (fh * 60 + fm) && nowM <= (th * 60 + tm);
};

const fmt = (t: string) => t.substring(0, 5);

const sortMenuItems = (items: MenuItem[]): MenuItem[] => {
    return [...items].sort((a, b) => {
        const aOrderable = a.is_available && isWithinAvailabilityWindow(a);
        const bOrderable = b.is_available && isWithinAvailabilityWindow(b);
        if (aOrderable && !bOrderable) return -1;
        if (!aOrderable && bOrderable) return 1;
        return 0;
    });
};

interface CategoryTab {
    id:        number | null;
    name:      string;
    sortOrder: number;
}

const extractCategories = (items: MenuItem[]): CategoryTab[] => {
    const map = new Map<number | null, CategoryTab>();
    for (const item of items) {
        const cat = (item as any).category;
        const key = cat?.id ?? null;
        if (!map.has(key)) {
            map.set(key, {
                id:        key,
                name:      cat?.name ?? 'Other',
                sortOrder: cat?.sort_order ?? 9999,
            });
        }
    }
    return Array.from(map.values()).sort((a, b) => {
        if (a.id === null) return 1;
        if (b.id === null) return -1;
        return a.sortOrder - b.sortOrder;
    });
};

const Pill: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({ label, active, onClick }) => (
    <button
        onClick={onClick}
        style={{
            whiteSpace: "nowrap",
            padding: "7px 16px",
            borderRadius: 8,
            border: `1.5px solid ${active ? BRAND : "#EDF1F7"}`,
            background: active ? BRAND : "#F7F9FC",
            color: active ? "#fff" : "#8F9BB3",
            fontWeight: 600,
            fontSize: 13,
            cursor: "pointer",
            flexShrink: 0,
        }}
    >
        {label}
    </button>
);

// ── Main Page ────────────────────────────────────────────────────
const RestaurantMenuPage: React.FC = () => {
    const { restaurant_id } = useParams<{ restaurant_id: string }>();
    const restaurantId = Number(restaurant_id);
    const navigate = useNavigate();
    const screens = useBreakpoint();

    const [loading,       setLoading]       = useState(false);
    const [restaurant,    setRestaurant]    = useState<Restaurant | null>(null);
    const [menuItemList,  setMenuItemList]  = useState<MenuItem[]>([]);
    const [categoryTabs,  setCategoryTabs]  = useState<CategoryTab[]>([]);
    const [selectedCatId, setSelectedCatId] = useState<number | null | 'ALL'>('ALL');
    const [cartCount,     setCartCount]     = useState(0);
    const [cartTotal,     setCartTotal]     = useState(0);

    const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
    const [modalVisible, setModalVisible] = useState(false);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            RestaurantService.getMenuItemsByRestaurantId(restaurantId),
            OrderService.readCartByUser(),
        ]).then(([menuItems, orderItems]) => {
            const sorted = sortMenuItems(menuItems);
            setMenuItemList(sorted);
            setCategoryTabs(extractCategories(sorted));

            const count = orderItems.reduce((sum: number, oi: OrderItem) => sum + oi.quantity, 0);
            setCartCount(count);
            let totalPrice = 0;
            orderItems.forEach((orderItem: OrderItem) => {
                const unitPrice = orderItem.flavour?.price ?? 0;
                totalPrice += (unitPrice / 100) * orderItem.quantity;
            });
            setCartTotal(totalPrice);
        }).finally(() => setLoading(false));
    }, [restaurantId]);

    const handleItemPress = (item: MenuItem) => {
        if (!item.is_available || !isWithinAvailabilityWindow(item)) return;
        setSelectedItem(item);
        setModalVisible(true);
    };

    const handleAddToCart = async (item: MenuItem, quantity: number, flavour: Flavour | null, unitPrice: number) => {
        const orderItem = new OrderItem();
        orderItem.menu_item = item;
        orderItem.flavour   = flavour ?? new Flavour();
        orderItem.quantity  = quantity;

        try {
            await OrderService.addToCart(orderItem);

            // Refetch rather than incrementing local counters — the backend
            // is the source of truth for pricing/fees, so trusting it avoids
            // the cart drifting out of sync with whatever it actually charges.
            const items = await OrderService.readCartByUser();
            const total = items.reduce((sum: number, oi: OrderItem) => {
                const price = oi.flavour?.price ?? 0;
                return sum + (price / 100) * oi.quantity;
            }, 0);
            const count = items.reduce((sum: number, oi: OrderItem) => sum + oi.quantity, 0);

            setCartCount(count);
            setCartTotal(total);
            console.log('🟢 cartCount just set to:', count);
        } catch (err) {
            message.error("Couldn't add that to your cart — please try again.");
        }
    };

    const navigateToCheckout = () => {
        navigate(`/cart/${restaurantId}`);
    };

    const filteredItems = selectedCatId === 'ALL'
        ? menuItemList
        : menuItemList.filter(item => {
            const cat = (item as any).category;
            const key = cat?.id ?? null;
            return key === selectedCatId;
        });

    const renderMenuItem = (item: MenuItem) => {
        const baseFlavour  = item.flavours?.[0];
        const displayPrice = baseFlavour?.price ? (baseFlavour.price / 100).toFixed(2) : '—';

        const hasWindow       = !!(item.available_from && item.available_till);
        const withinWindow    = isWithinAvailabilityWindow(item);
        const isOrderable     = item.is_available && withinWindow;
        const isOutOfStock    = !item.is_available;
        const isOutsideWindow = item.is_available && !withinWindow;

        return (
            <Col key={item.id} xs={24} sm={12} lg={8}>
                <Flex
                    gap={12}
                    onClick={() => handleItemPress(item)}
                    style={{
                        cursor: isOrderable ? "pointer" : "default",
                        padding: "16px 0",
                        borderBottom: "1px solid #EDF1F7",
                        opacity: isOrderable ? 1 : 0.6,
                    }}
                >
                    <div style={{
                        width: 96, height: 96, minWidth: 96, borderRadius: 12,
                        background: BRAND_LIGHT, border: "1.5px solid #BFEAD8",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        overflow: "hidden",
                    }}>
                        {item.image_url ? (
                            <img src={item.image_url} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                            <span style={{ fontSize: 40 }}>🍗</span>
                        )}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong style={{ display: "block", fontSize: 16, color: "#1A1A2E", marginBottom: 2 }}>
                            {item.name}
                        </Text>
                        {item.description && (
                            <Text
                                type="secondary"
                                style={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    fontSize: 13,
                                    marginBottom: 6,
                                }}
                            >
                                {item.description}
                            </Text>
                        )}

                        <Flex align="center" justify="space-between" wrap="wrap" gap={8}>
                            <Text strong style={{ color: BRAND, fontSize: 15 }}>R {displayPrice}</Text>

                            {isOutOfStock && (
                                <Text strong style={{ color: "#FF3D71", fontSize: 13 }}>Out of Stock</Text>
                            )}
                            {isOutsideWindow && hasWindow && (
                                <Flex align="center" gap={4}>
                                    <ClockCircleOutlined style={{ color: "#FF9500", fontSize: 13 }} />
                                    <Text strong style={{ color: "#FF9500", fontSize: 13 }}>
                                        {fmt(item.available_from!)} – {fmt(item.available_till!)}
                                    </Text>
                                </Flex>
                            )}
                            {isOrderable && hasWindow && (
                                <Flex align="center" gap={4}>
                                    <ClockCircleOutlined style={{ color: BRAND, fontSize: 13 }} />
                                    <Text strong style={{ color: BRAND, fontSize: 13 }}>
                                        Till {fmt(item.available_till!)}
                                    </Text>
                                </Flex>
                            )}
                        </Flex>
                    </div>
                </Flex>
            </Col>
        );
    };

    return (
        <>
            <TopNavigation cartItemCount={cartCount} onCartClick={navigateToCheckout} />

            <div style={{ paddingTop: HEADER_HEIGHT, background: "#fff", minHeight: "100dvh" }}>

                {categoryTabs.length > 0 && (
                    <div style={{
                        position: "sticky", top: HEADER_HEIGHT, zIndex: 20,
                        background: "#fff", borderBottom: "1px solid #EDF1F7",
                        padding: "12px 0",
                    }}>
                        <Flex gap={8} style={{ overflowX: "auto", padding: `0 ${screens.xs ? 16 : 24}px` }}>
                            <Pill label="All" active={selectedCatId === 'ALL'} onClick={() => setSelectedCatId('ALL')} />
                            {categoryTabs.map(cat => (
                                <Pill
                                    key={cat.id ?? 'other'}
                                    label={cat.name}
                                    active={selectedCatId === cat.id}
                                    onClick={() => setSelectedCatId(cat.id)}
                                />
                            ))}
                        </Flex>
                    </div>
                )}

                <div style={{
                    padding: `24px clamp(16px, 4vw, 24px) ${cartCount > 0 ? 100 : 40}px`,
                    maxWidth: 1200,
                    margin: "0 auto",
                }}>
                    <Title level={3} style={{ margin: "0 0 20px" }}>
                        {restaurant?.name ?? "Menu"}
                    </Title>

                    {loading ? (
                        <Flex justify="center" style={{ padding: "80px 0" }}>
                            <Spin size="large" />
                        </Flex>
                    ) : filteredItems.length === 0 ? (
                        <Flex vertical align="center" style={{ padding: "80px 0" }}>
                            <Text type="secondary">No items in this category.</Text>
                        </Flex>
                    ) : (
                        <Row gutter={[24, 0]}>
                            {filteredItems.map(renderMenuItem)}
                        </Row>
                    )}
                </div>
            </div>

            {cartCount > 0 && (
                <div style={{
                    position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 30,
                    display: "flex", justifyContent: "center",
                    padding: "0 16px 16px",
                    pointerEvents: "none",
                }}>
                    <Button
                        type="primary"
                        onClick={navigateToCheckout}
                        style={{
                            pointerEvents: "auto",
                            background: BRAND, borderColor: BRAND,
                            height: 56, borderRadius: 12, width: "100%", maxWidth: 480,
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "0 20px",
                            boxShadow: "0 6px 20px rgba(0,175,102,0.35)",
                        }}
                    >
                        <span style={{ fontWeight: 800 }}>{cartCount} Items</span>
                        <span style={{ fontWeight: 800 }}>Checkout</span>
                        <span style={{ fontWeight: 700 }}>R {cartTotal.toFixed(2)}</span>
                    </Button>
                </div>
            )}

            <MenuItemModal
                visible={modalVisible}
                item={selectedItem}
                onClose={() => setModalVisible(false)}
                onAddToCart={handleAddToCart}
            />

            <Footer />
        </>
    );
};

export default RestaurantMenuPage;